import { Component, OnDestroy, signal, effect, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { BusyIndicatorMessageManager, BusyIndicatorService, BusyIndicatorModule } from '../../../Busy-Indicator';
import { FilingBatchRepository } from '../../Repositories';
import { WeissmanMutexService, IMutexServiceHandler } from '../../WeissmanMutexService';
import { BaseExpandableComponent } from '../../../UI-Lib/Expandable-Component/baseExpandableComponent';
import { MessageModalService } from '../../../UI-Lib/Message-Box/messageModal.service';
import { FilingBatchService } from '../filingBatch.service';
import { FilingBatchInfoComponent } from './Filing-Batch-Info/filingBatchInfo.component';
import { HelpModule } from '../../../UI-Lib/Help-Tooltip/help.module';

/**
 * Details-page panel chrome (header, edit/save/cancel, expand/collapse, delete) for the
 * `FilingBatchInfoComponent` shared form. Owns the mutex-based edit-mode toggle and busy
 * indicator for the details-page context; the create modal has its own, simpler chrome.
 */
@Component({
    selector: 'filing-batch-info-panel',
    templateUrl: './filingBatchInfoPanel.component.html',
    styleUrls: ['./filingBatchInfoPanel.component.scss'],
    standalone: true,
    imports: [CommonModule, HelpModule, BusyIndicatorModule, FilingBatchInfoComponent],
})
export class FilingBatchInfoPanelComponent extends BaseExpandableComponent implements OnDestroy, IMutexServiceHandler {
    constructor(
        private readonly _filingBatchService: FilingBatchService,
        private readonly _filingBatchRepository: FilingBatchRepository,
        private readonly _mutexService: WeissmanMutexService,
        private readonly _messageModalService: MessageModalService,
        private readonly _busyIndicatorService: BusyIndicatorService,
    ) {
        super(_filingBatchService, 'filing-batch-info');

        effect(() => {
            if (!this.editMode()) {
                this._mutexService.release(this, this._filingBatchService.editGroup);
            }
        });

        // Keeps the model displayed by the child form in sync whenever the input model changes
        // (e.g. after a successful save re-binds `filingBatch` with the server response).
        effect(() => {
            this.displayModel.set(this.filingBatch());
        });
    }

    filingBatch = input<Compliance.FilingBatchModel>();
    canEdit = input<boolean>(false);

    filingBatchUpdated = output<Compliance.FilingBatchModel>();
    filingBatchDeleted = output<Compliance.FilingBatchModel>();

    busyMessageManager = new BusyIndicatorMessageManager<string>();

    readonly editMode = signal(false);
    readonly formValid = signal(false);
    readonly displayModel = signal<Compliance.FilingBatchModel>(undefined);

    private _workingModel: Compliance.FilingBatchModel;

    get canEnterEditMode(): boolean {
        return this._mutexService.canAcquire(this._filingBatchService.editGroup);
    }

    get isReadOnly(): boolean {
        return this._filingBatchService.isReadOnly(this.filingBatch()?.processStatus);
    }

    get editHelpContentId(): string {
        if (!this.canEnterEditMode) {
            return 'app.disabled-edit-mode';
        }

        return 'app.edit';
    }

    ngOnDestroy(): void {
        this._mutexService.release(this, this._filingBatchService.editGroup);
    }

    wsMutexRelease(_groupId: string): Promise<void> {
        return Promise.resolve();
    }

    edit(): void {
        if (!this.canEdit()) {
            return;
        }

        this._workingModel = this.filingBatch();
        this._mutexService.acquire(this, this._filingBatchService.editGroup);
        this.editMode.set(true);
    }

    onFilingBatchChange(model: Compliance.FilingBatchModel): void {
        this._workingModel = model;
    }

    async save(): Promise<void> {
        if (!this._workingModel) {
            return;
        }

        const busyMessageId = 'saving';
        this.busyMessageManager.add('Saving', busyMessageId);

        try {
            const savedModel = await lastValueFrom(this._filingBatchRepository.update(this._workingModel));

            this.editMode.set(false);
            this.filingBatchUpdated.emit(savedModel);
        } finally {
            this.busyMessageManager.remove(busyMessageId);
        }
    }

    cancel(): void {
        this.displayModel.set({ ...this.filingBatch() });
        this.editMode.set(false);
    }

    async delete(): Promise<void> {
        const deleted = await this._deleteFilingBatch(this.filingBatch());
        if (deleted) {
            this.filingBatchDeleted.emit(this.filingBatch());
        }
    }

    private async _deleteFilingBatch(filingBatch: Compliance.FilingBatchModel): Promise<boolean> {
        try {
            await this._messageModalService.confirm(
                'Are you sure you wish to delete the filing batch?',
                'Confirm Delete',
            );
        } catch (_e) {
            return Promise.resolve(false);
        }

        let busyRef = this._busyIndicatorService.show({ message: 'Deleting' });

        try {
            await lastValueFrom(this._filingBatchRepository.delete(filingBatch.filingBatchId, false));
        } catch (e2) {
            // service returns a 422 and a message if user confirmation needed to force delete
            if (e2 && e2.status !== 422) {
                return Promise.reject(e2);
            }
            busyRef.hide();

            try {
                await this._messageModalService.confirm(e2.error.message, 'Confirm Delete');
            } catch (_e3) {
                return Promise.resolve(false);
            }

            busyRef = this._busyIndicatorService.show({ message: 'Deleting' });

            await lastValueFrom<boolean>(this._filingBatchRepository.delete(filingBatch.filingBatchId, true));
        } finally {
            busyRef.hide();
        }

        return Promise.resolve(true);
    }
}
